import React, { Component } from 'react';

import { RouteComponentProps } from '@reach/router';

import { IClubsActions } from '../../../store/player/clubs/actions';
import { IEventsActions } from '../../../store/player/events/actions';
import { IProfileActions } from '../../../store/user/actions';
import { IAuthStore } from '../../../store/auth/store';
import { INotificationsActions } from '../../../store/notification/actions';
import { IClubsStore } from '../../../store/player/clubs/store';
import { IEventsStore } from '../../../store/player/events/store';
import { IInvitationStore } from '../../../store/player/invitation/store';
import { ISource, IStripeToken, IUserStore } from '../../../store/user/store';

import { withContext } from '../../../contrib/utils';

import { ICheckoutState } from './EventCheckout/EventCheckout';
import EventInfo from './EventInfo';
import EventStats from './EventStats';

import { EventHomeCards } from './Components/EventHomeCards';
import { EventHomeViews } from './Utils/EventHomeViews';
import { sendErrorPushNotification } from './Utils/sendErrorPushNotification';
import { sendSuccessPushNotification } from './Utils/sendSuccessPushNotification';

interface IProps extends RouteComponentProps {
  actions: {
    clubs: IClubsActions;
    events: IEventsActions;
    notifications: INotificationsActions;
    user: IProfileActions;
  };
  auth: IAuthStore;
  clubs: IClubsStore;
  eventId: string;
  events: IEventsStore;
  user: IUserStore;
  invitation?: IInvitationStore;
  location: any;
}

interface IState {
  checkout: ICheckoutState;
  creditCardFormReady: boolean;
  loading: boolean;
  noteMessage: string;
  selectedCard: EventHomeViews;
  source?: ISource;
}

class EventHomeComponent extends Component<IProps, IState> {
  public cardElement: any;

  constructor(props: IProps) {
    super(props);

    const {
      actions: {
        clubs: { all: getAllClubs }
      },
      invitation,
      events,
      eventId
    } = props;

    getAllClubs();

    this.cardElement = React.createRef();

    const eventHasProcessPayment =
      events && events[Number(eventId)] && events[Number(eventId)].processPayment;

    const isInvitationForAnEvent = invitation && invitation.event;

    const invitationClubHasProcessPayment = invitation && invitation.club.processPayment;

    this.state = {
      checkout: {
        selectedAddons: {}
      },
      creditCardFormReady: false,
      loading: false,
      noteMessage: '',
      selectedCard: isInvitationForAnEvent
        ? invitationClubHasProcessPayment && eventHasProcessPayment
          ? EventHomeViews.CHECKOUT
          : EventHomeViews.CONFIRM
        : EventHomeViews.STATS
    };
  }

  public componentDidMount(): void {
    if (this.props.invitation && this.props.invitation.event) {
      this.selectCard(EventHomeViews.CHECKOUT);
    }
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.eventId !== this.props.eventId) {
      props.actions.events.get(props.eventId).catch();
    }
  }

  public selectCard = (cardIndex: number) => () => this.setState({ selectedCard: cardIndex });

  public saveCheckout = (checkout: ICheckoutState): void => {
    this.setState({ checkout, selectedCard: EventHomeViews.SELECT_PAYMENT });
  };

  public getRegistrationSummary = async (
    checkout: ICheckoutState,
    couponCode: string = ''
  ): Promise<any> => {
    const { selectedAddons, coupon } = checkout;
    const event = Number(this.props.eventId);
    const addons = Object.keys(selectedAddons)
      .filter(addonId => selectedAddons[Number(addonId)])
      .map(addonId => Number(addonId));

    return this.props.actions.events.getRegistrationSummary(
      event,
      addons,
      coupon ? coupon.codename : couponCode
    );
  };

  public onSelectPayment = (source: ISource) => () => {
    this.setState({ source, selectedCard: EventHomeViews.CONFIRM });
  };

  public onCardDone = (): void => {
    this.setState({ loading: true });
    if (this.cardElement.current) {
      this.cardElement.current.submit();
    }
  };

  public onCardSubmit = async (token: IStripeToken): Promise<any> => {
    const {
      actions,
      actions: {
        notifications: { push: pushNotification }
      }
    } = this.props;
    const { errors } = await actions.user.addCustomerSource(token);

    if (errors) {
      await sendErrorPushNotification(pushNotification, errors);
    } else {
      await sendSuccessPushNotification(pushNotification, 'New payment method was added');
    }

    this.setState({
      loading: false,
      selectedCard: EventHomeViews.CONFIRM,
      source: token.card
    });
  };

  public onCardComplete = (completed: boolean): void => {
    this.setState({ creditCardFormReady: completed });
  };

  public onMessageChange = (event: any): void => {
    this.setState({ noteMessage: event.target.value });
  };

  public sendMessage = async (): Promise<any> => {
    const {
      actions,
      actions: {
        notifications: { push: pushNotification }
      }
    } = this.props;
    const { errors } = await actions.events.setMessage(
      Number(this.props.eventId),
      this.state.noteMessage
    );

    if (errors) {
      await sendErrorPushNotification(pushNotification, errors);
    } else {
      await sendSuccessPushNotification(pushNotification, 'Message Sent');

      this.setState({ selectedCard: EventHomeViews.SENT_MESSAGE });
    }
  };

  public onPaymentSuccess = async (): Promise<void> => {
    const {
      checkout: { selectedAddons, coupon },
      source
    } = this.state;

    const {
      actions: {
        notifications: { push: pushNotification }
      }
    } = this.props;

    const event = Number(this.props.eventId);
    const addons = Object.keys(selectedAddons)
      .filter(addonId => selectedAddons[Number(addonId)])
      .map(addonId => Number(addonId));
    const { errors } = await this.props.actions.events.register(
      event,
      addons,
      coupon ? coupon.codename : '',
      source ? source.djstripeId : undefined
    );

    if (!errors) {
      this.props.actions.events.get(this.props.eventId, true).catch();
      this.setState({ selectedCard: EventHomeViews.SUCCESS });
    } else {
      await sendErrorPushNotification(pushNotification, errors);
    }
  };

  public onConfirmSuccess = async (): Promise<void> => {
    const {
      actions: {
        notifications: { push: pushNotification }
      }
    } = this.props;

    const event = Number(this.props.eventId);
    const { errors } = await this.props.actions.events.register(event, [], '');

    if (!errors) {
      this.props.actions.events.get(this.props.eventId, true).catch();
      this.setState({ selectedCard: EventHomeViews.MESSAGE });
    } else {
      await sendErrorPushNotification(pushNotification, errors);
    }
  };

  public onWithdraw = async (): Promise<void> => {
    const {
      actions,
      actions: {
        notifications: { push: pushNotification }
      }
    } = this.props;
    const { errors } = await actions.events.withdraw(Number(this.props.eventId));

    if (errors) {
      await sendErrorPushNotification(pushNotification, errors);
    } else {
      await sendSuccessPushNotification(pushNotification, 'Withdrawal processed successfully');

      this.setState({ selectedCard: EventHomeViews.CONFIRMATION_WITHDRAW });
    }
  };

  public render(): JSX.Element {
    const {
      auth: { isAuthenticated },
      eventId,
      user,
      events
    } = this.props;
    const { checkout, creditCardFormReady, noteMessage, selectedCard } = this.state;
    const event = events[Number(eventId)];

    return (
      <div className="home event-home wrapper">
        {event && (
          <div className="event-home-body">
            <EventHomeCards
              user={user}
              event={event}
              isAuthenticated={isAuthenticated}
              cardElement={this.cardElement}
              checkout={checkout}
              creditCardFormReady={creditCardFormReady}
              eventId={eventId}
              getRegistrationSummary={this.getRegistrationSummary}
              noteMessage={noteMessage}
              onCardComplete={this.onCardComplete}
              onCardDone={this.onCardDone}
              onCardSubmit={this.onCardSubmit}
              onMessageChange={this.onMessageChange}
              onPaymentSuccess={this.onPaymentSuccess}
              onSelectPayment={this.onSelectPayment}
              onWithdraw={this.onWithdraw}
              saveCheckout={this.saveCheckout}
              selectCard={this.selectCard}
              selectedCard={selectedCard}
              sendMessage={this.sendMessage}
            />

            {selectedCard === EventHomeViews.STATS && (
              <>
                <EventStats item={event} />

                <EventInfo item={event} />
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

const EventHome = withContext(EventHomeComponent, 'clubs', 'events', 'auth', 'user', 'invitation');

export { EventHome, EventHomeComponent };
